import React, { useState, useRef, useEffect } from "react";
import { CheckCircleIcon, FileTextIcon, UploadIcon, Loader, RefreshCwIcon } from "lucide-react";
import { uploadCV } from "@service/cv";
import { Badge } from "@component/badge";
import { Button } from "@component/button"
import { CompanyPreferences } from "@type/company";

interface ResumeUploadProps {
  isLoading?: boolean;
  cvData?: any;
  onComplete?: () => void;
  onUploadSuccess?: (data: any) => void;
  isShowBrand?: boolean;
  companyPreferences?: CompanyPreferences | undefined
}

export default function ResumeUpload({
  companyPreferences = undefined,
  isShowBrand = false,
  isLoading = false,
  cvData = null,
  onComplete,
  onUploadSuccess,
}: ResumeUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [isReplacing, setIsReplacing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Debug: Log cvData when component mounts or when cvData changes
  useEffect(() => {
    console.log("ResumeUpload component - cvData:", cvData);
  }, [cvData]);

  // Check if cvData has the expected nested structure
  const hasCvData = cvData && (cvData.CV_data || cvData.cv_data);

  console.log("Has CV data:", hasCvData);

  // Extract the nested CV data if available
  const cv = hasCvData ? (cvData.CV_data || cvData.cv_data) : null;
  const summary = hasCvData ? (cvData.summary_data || cvData.summaryData) : null;

  // Format date helper
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Handle file change
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("File size must be less than 5MB.");
      return;
    }

    if (!["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/rtf"].includes(file.type)) {
      setUploadError("Please upload a PDF, DOCX, or RTF file.");
      return;
    }

    setIsUploading(true);
    setIsReplacing(hasCvData); // Set replacing flag if we already have a CV
    setUploadError("");

    try {
      const response = await uploadCV(file);
      console.log("Upload CV response:", response);

      if (response.success) {
        // Explicitly log the callback invocation
        console.log("Calling onUploadSuccess with:", response.data);

        if (onUploadSuccess) {
          onUploadSuccess(response.data);
        }

        // Clear the file input for future uploads
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setUploadError(response.message || "Failed to upload resume.");
      }
    } catch (error) {
      console.error("Error uploading resume:", error);
      setUploadError("An unexpected error occurred.");
      if (onUploadSuccess) {
        onUploadSuccess(null);
      }
    } finally {
      setIsUploading(false);
      setIsReplacing(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Generate resume display data
  const resumeData = hasCvData ? {
    fileName: cv.name ? `${cv.name}'s Resume.pdf` : "Resume.pdf",
    position: cv.position || "",
    email: cv.email || "",
    phone: cv.phone || "",
    skills: cv.skills || [],
    experience: cv.work_experience ? cv.work_experience.length : 0,
    education: cv.education && cv.education.length > 0 ? cv.education[0].degree : "",
    uploadDate: formatDate(new Date().toISOString()),
  } : {
    fileName: "No resume uploaded",
    uploadDate: "",
    fileSize: "",
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
        <div className="flex justify-center items-center py-12">
          <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          <span className="ml-2">Loading your resume...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" key={hasCvData ? "has-cv" : "no-cv"}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">
          Resume Upload
        </h2>
        {hasCvData && (
          <span className="flex items-center text-green-600 text-sm font-medium">
            <CheckCircleIcon className="w-5 h-5 mr-1" />
            Resume Uploaded
          </span>
        )}
      </div>

      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        {hasCvData ? (
          <div className="flex flex-col">
            <div className="flex items-start mb-4">
              <div className="bg-blue-100 p-3 rounded-lg mr-4">
                <FileTextIcon className="w-8 h-8 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-lg">
                  {resumeData.fileName}
                </h3>
                <p className="text-gray-500 text-sm">
                  {resumeData.position && <span className="font-medium">{resumeData.position}</span>}
                </p>
                <p className="text-gray-500 text-sm">
                  Uploaded on {resumeData.uploadDate}
                </p>
              </div>
            </div>

            {/* Resume Details */}
            <div className="mt-3 border-t pt-3">
              {resumeData.email && (
                <div className="text-sm mb-1">
                  <span className="font-medium">Email:</span> {resumeData.email}
                </div>
              )}
              {resumeData.phone && (
                <div className="text-sm mb-1">
                  <span className="font-medium">Phone:</span> {resumeData.phone}
                </div>
              )}

              {summary && summary.years_of_experience > 0 && (
                <div className="text-sm mb-1">
                  <span className="font-medium">Experience:</span> {summary.years_of_experience} years
                </div>
              )}

              {resumeData.education && (
                <div className="text-sm mb-1">
                  <span className="font-medium">Education:</span> {resumeData.education}
                </div>
              )}

              {resumeData.skills && resumeData.skills.length > 0 && (
                <div className="mt-2">
                  <div className="font-medium text-sm">Skills:</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {resumeData.skills.map((skill: string, index: number) => (
                      <Badge key={skill} variant="outline" id={`tot0ui_${index}`}>
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="mt-4 flex space-x-3">
              <button
                className={`px-3 py-1 text-sm border border-gray-300 rounded-md transition-colors flex items-center ${isReplacing
                  ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                  : "hover:bg-gray-100"
                  }`}
                onClick={handleUploadClick}
                disabled={isReplacing}
              >
                {isReplacing ? (
                  <>
                    <RefreshCwIcon className="w-4 h-4 mr-1 animate-spin" />
                    Replacing...
                  </>
                ) : (
                  <>
                    <UploadIcon className="w-4 h-4 mr-1" />
                    Replace Resume
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.rtf"
                  className="hidden"
                  onChange={handleFileChange}
                  disabled={isReplacing}
                />
              </button>
            </div>

            {/* Display error if there's an issue with replacement */}
            {uploadError && (
              <div className="mt-2 text-red-500 text-sm">
                {uploadError}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <FileTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />

            <h3 className="font-medium text-lg mb-2">
              Upload Your Resume
            </h3>
            <p className="text-gray-500 mb-4">
              Supported formats: PDF, DOCX, RTF (Max size: 5MB)
            </p>
            {uploadError && (
              <p className="text-red-500 mb-4">{uploadError}</p>
            )}
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center mx-auto"
              onClick={handleUploadClick}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader className="animate-spin w-4 h-4 mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <UploadIcon className="w-4 h-4 mr-2" />
                  Upload Resume
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.rtf"
                className="hidden"
                onChange={handleFileChange}
              />
            </button>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button
          variant="default"
          className={`${!isShowBrand && "bg-blue-600"} text-white hover:${!isShowBrand && "bg-blue-700"}`}
          style={isShowBrand && companyPreferences ? {
            backgroundColor: companyPreferences.primary_color,
          } : undefined}
          onClick={onComplete}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
